import { useMutation } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { InsertCustomManufacturerInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core'
import { InsertCustomManufacturerInProductListingMutation } from 'api/graphQL/mutations/projectNugget.mutations'
import clsx from 'clsx'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import { useNotification } from 'components/WorkspaceEditor/services'
import React, { useMemo } from 'react'
import type { ExistingCustomManufacturer } from './CustomManufacturerFieldsWrapper'
import { useCustomManufacturerCache } from './hooks/useCustomManufacturerCache'

const PLACEHOLDER = 'Insert Manufacturers Name'
const MAX_FIELDS = 3
const INPUT_BORDER_COLOR = '#0D7B55'
const INACTIVE_COLOR = 'rgba(0,0,0,0.5)'
const SAVE_SUCCESS_MESSAGE = 'Changes Saved Successfully'
const SAVING_MESSAGE = 'Saving...'

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'block',
        marginTop: theme.spacing(0.5),
        paddingLeft: theme.spacing(5),
    },
    inputBox: {
        width: 180,
        fontSize: '1.4rem',
        background: '#FFFFFF',
        borderRadius: pxToRem(4),
        lineHeight: '16px',
        height: 'fit-content',
        outline: 'none',
        display: 'block',
        border: `1px solid ${INPUT_BORDER_COLOR}`,
        color: theme.palette.common.black,
        padding: theme.spacing(0.375, 0.5),
        marginBottom: theme.spacing(0.5),
        whiteSpace: 'nowrap',
        '&::placeholder': {
            color: 'rgba(0, 0, 0, 0.4)',
            fontStyle: 'italic',
        },
    },
    // Mirrors disabledOptionGroup + disabledBoxedInputContainer from useOptionGroupStyles —
    // same inactive treatment as ReplacementOption when its parent paragraph is hidden.
    inputBoxInactive: {
        textDecoration: 'line-through',
        textDecorationColor: `${INACTIVE_COLOR} !important`,
        color: `${INACTIVE_COLOR} !important`,
        border: `1px solid ${INACTIVE_COLOR}`,
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },
}))

interface CustomManufacturerFieldsProps {
    elementId: string
    productFamilyId: string
    projectId: string
    onManufacturerAdded?: () => void
    isHidden?: boolean
    rootArticleId?: string
    parentElementId?: string
}

export const CustomManufacturerFields: React.FC<CustomManufacturerFieldsProps> = ({
    elementId,
    productFamilyId,
    projectId,
    onManufacturerAdded,
    isHidden = false,
    rootArticleId,
    parentElementId,
}) => {
    const classes = useStyles()
    const { fetchTrackChangesDataWithApolloClient } = useTrackChangesQuery()
    const {
        selectedProjectElementNode,
        activeTab,
        treeData,
        setTreeData,
        contentItemViewNodes,
        setContentIdsToReplaceOnNextFetch,
    } = useShallowSelector((state) => ({
        selectedProjectElementNode: state.selectedProjectElementNode,
        activeTab: state.activeTab,
        treeData: state.treeData,
        setTreeData: state.actions.setTreeData,
        contentItemViewNodes: state.contentItemViewNodes,
        setContentIdsToReplaceOnNextFetch:
            state.actions.setContentIdsToReplaceOnNextFetch,
    }))
    const notification = useNotification()
    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        projectId: projectId!,
        overrideContentItemNodes: !treeData?.length,
    })
    const [fields, setFields] = React.useState<string[]>(() =>
        Array<string>(MAX_FIELDS).fill('')
    )
    const [
        updateManufacturerLoading,
        setUpdateManufacturerLoading,
    ] = React.useState<boolean>(false)

    const hasChangesRef = React.useRef<boolean[]>(
        Array<boolean>(MAX_FIELDS).fill(false)
    )
    const fieldsRef = React.useRef<string[]>(Array<string>(MAX_FIELDS).fill(''))
    // Guards setState in handleBlur after unmount (React 17 warning).
    const isMountedRef = React.useRef(true)
    // Custom-mfr pills owned by the MFR/BOD paragraph this input-field row
    // belongs to (Bug 2690689). Without dtvParentId scoping, every
    // per-PT row at family level would read from the global slice and show
    // other paragraphs' pills. Dedup is order-dependent — the first pill for
    // a given pillId wins.
    const existingCustomManufacturers = useMemo<
        ExistingCustomManufacturer[]
    >(() => {
        const existingData = new Set<string>()

        return (
            contentItemViewNodes?.reduce<ExistingCustomManufacturer[]>(
                (acc, node) => {
                    if (
                        !node.isCustomManufacturer ||
                        node.isAddedInOfficeMasterDraftProject ||
                        (parentElementId !== undefined &&
                            node.dtvParentId !== parentElementId)
                    ) {
                        return acc
                    }
                    const id = node.pillId ?? ''
                    if (!id || existingData.has(id)) return acc
                    existingData.add(id)
                    acc.push({ name: node.elements?.[0]?.text ?? '', id })
                    return acc
                },
                []
            ) ?? []
        )
    }, [contentItemViewNodes, parentElementId])
    const { cacheKeysFor, readSlotValue, cache } = useCustomManufacturerCache({
        elementId,
        parentElementId,
    })
    // Cached typed value wins over server pill name (Bug 2690577, 2706261).
    React.useEffect(() => {
        const newFields = Array.from({ length: MAX_FIELDS }, (_, i) =>
            readSlotValue(i, existingCustomManufacturers[i])
        )
        fieldsRef.current = newFields
        setFields(newFields)
    }, [existingCustomManufacturers, readSlotValue])
    const [insertCustomManufacturerInProductListing] = useMutation<
        { insertCustomManufacturerInProductListing: ContentItem[] },
        { input: Partial<InsertCustomManufacturerInput> }
    >(InsertCustomManufacturerInProductListingMutation, {
        onError: (error) => {
            notification.error({ message: error.message ?? '' })
        },
        onCompleted: () => {
            onManufacturerAdded?.()
            notification.success({ message: SAVE_SUCCESS_MESSAGE })
        },
    })

    const handleChange = React.useCallback(
        (index: number, value: string) => {
            setFields((prev) => {
                const next = [...prev]
                next[index] = value
                fieldsRef.current = next
                return next
            })
            hasChangesRef.current[index] = true
            const pillId = existingCustomManufacturers[index]?.id
            for (const key of cacheKeysFor(index, pillId)) {
                cache.set(key, value)
            }
        },
        [existingCustomManufacturers, cacheKeysFor, cache]
    )

    const handleBlur = React.useCallback(
        async (index: number) => {
            if (!hasChangesRef.current[index]) {
                return
            }

            const existing = existingCustomManufacturers[index]
            const manufacturerName = fieldsRef.current[index]?.trim() ?? ''

            hasChangesRef.current[index] = false
            if (isMountedRef.current) setUpdateManufacturerLoading(true)
            notification.loading({ message: SAVING_MESSAGE })

            try {
                await insertCustomManufacturerInProductListing({
                    variables: {
                        input: {
                            elementId,
                            isBOD: false,
                            manufacturerName,
                            productFamilyId,
                            projectId,
                            ...(existing?.id
                                ? { customManufacturerProductId: existing.id }
                                : {}),
                        },
                    },
                })
                // Server deletes the pill on empty-name; the next fetch's
                // response omits it. mergeContentItems otherwise keeps any
                // existing node that's not in the response, so we mark the
                // pill ID for removal via the same channel BP sync uses. The
                // fetcher consumes and self-clears the list on next fetch.
                if (manufacturerName === '' && existing?.id) {
                    setContentIdsToReplaceOnNextFetch?.([existing.id])
                }
                if (!rootArticleId) return
                await getContentItemViewNodesWithApolloClient(
                    [rootArticleId],
                    activeTab
                )

                // Drop the override so a twin-pill (MFR↔BOD) edit isn't masked.
                // Skip if user re-typed during the async window.
                for (const key of cacheKeysFor(index, existing?.id)) {
                    if (cache.get(key) === manufacturerName) {
                        cache.delete(key)
                    }
                }

                // *WithApolloClient variant is documented in useTrackChangesQuery
                // as the correct choice inside mutation callbacks (promise-based,
                // independent of React render cycles).
                await fetchTrackChangesDataWithApolloClient()
            } catch {
                // useMutation.onError already surfaced the notification.
            } finally {
                if (isMountedRef.current) setUpdateManufacturerLoading(false)
            }
        },
        [
            existingCustomManufacturers,
            elementId,
            productFamilyId,
            projectId,
            insertCustomManufacturerInProductListing,
            fetchTrackChangesDataWithApolloClient,
            rootArticleId,
            activeTab,
            getContentItemViewNodesWithApolloClient,
            setContentIdsToReplaceOnNextFetch,
            notification,
            cacheKeysFor,
            cache,
        ]
    )

    // Save pending changes on unmount, hasChangesRef guards against double-saves.
    const handleBlurRef = React.useRef(handleBlur)
    handleBlurRef.current = handleBlur
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false
            for (let i = 0; i < MAX_FIELDS; i++) {
                if (hasChangesRef.current[i]) {
                    handleBlurRef.current(i)
                }
            }
        }
    }, [])

    return (
        <div className={classes.container}>
            {fields.map((value, index) => (
                <input
                    key={index}
                    data-testid={`custom-manufacturer-${index + 1}`}
                    className={clsx(
                        classes.inputBox,
                        isHidden && classes.inputBoxInactive
                    )}
                    placeholder={PLACEHOLDER}
                    value={value}
                    disabled={isHidden || updateManufacturerLoading}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onBlur={() => handleBlur(index)}
                    draggable={false}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.preventDefault()}
                />
            ))}
        </div>
    )
}
